import React, { createContext, useEffect, useState, ReactNode } from 'react';
import { CopilotState, ThemeOption } from '../types';
import { DEFAULTS, STORAGE_KEYS } from '../utils/config';

// Create the context with a default value
export const CopilotContext = createContext<CopilotState>({
  isOpen: false,
  toggleSidebar: () => {},
  openSidebar: () => {},
  closeSidebar: () => {},
  theme: DEFAULTS.THEME,
  setTheme: () => {},
  inputMessage: '',
  setInputMessage: () => {},
});

interface CopilotProviderProps {
  children: ReactNode;
  initialIsOpen?: boolean;
  initialTheme?: ThemeOption;
  persistState?: boolean;
}

export const CopilotProvider: React.FC<CopilotProviderProps> = ({
  children,
  initialIsOpen = false,
  initialTheme = DEFAULTS.THEME,
  persistState = true,
}) => {
  // Initialize state from localStorage if available
  const [isOpen, setIsOpen] = useState<boolean>(() => {
    if (persistState) {
      const savedState = localStorage.getItem(STORAGE_KEYS.SIDEBAR_STATE);
      return savedState ? JSON.parse(savedState) : initialIsOpen;
    }
    return initialIsOpen;
  });

  const [theme, setTheme] = useState<ThemeOption>(() => {
    if (persistState) {
      const savedTheme = localStorage.getItem(STORAGE_KEYS.THEME);
      return savedTheme ? (JSON.parse(savedTheme) as ThemeOption) : initialTheme;
    }
    return initialTheme;
  });

  // State for input message
  const [inputMessage, setInputMessage] = useState('');

  // Update localStorage when state changes
  useEffect(() => {
    if (persistState) {
      localStorage.setItem(STORAGE_KEYS.SIDEBAR_STATE, JSON.stringify(isOpen));
    }
  }, [isOpen, persistState]);

  useEffect(() => {
    if (persistState) {
      localStorage.setItem(STORAGE_KEYS.THEME, JSON.stringify(theme));
    }
  }, [theme, persistState]);

  // Toggle sidebar state
  const toggleSidebar = () => {
    setIsOpen((prev) => {
      const newState = !prev;
      // If opening the sidebar, focus the textarea after a delay
      if (newState) {
        setTimeout(() => {
          const textarea = document.querySelector('textarea');
          if (textarea) textarea.focus();
        }, 300); // Delay to ensure the sidebar is fully open
      }
      return newState;
    });
  };

  // Open sidebar
  const openSidebar = () => {
    setIsOpen(true);
    // Focus the textarea after opening the sidebar
    setTimeout(() => {
      const textarea = document.querySelector('textarea');
      if (textarea) textarea.focus();
    }, 300); // Delay to ensure the sidebar is fully open
  };

  // Close sidebar
  const closeSidebar = () => {
    setIsOpen(false);
  };

  // Update theme
  const handleSetTheme = (newTheme: ThemeOption) => {
    setTheme(newTheme);
  };

  // Context value
  const value: CopilotState = {
    isOpen,
    toggleSidebar,
    openSidebar,
    closeSidebar,
    theme,
    setTheme: handleSetTheme,
    inputMessage,
    setInputMessage,
  };

  return <CopilotContext.Provider value={value}>{children}</CopilotContext.Provider>;
};
